import type { FormArray } from '@angular/forms';
import { girthInches, lengthToInches } from './customer-special-handling';
import {
  CUSTOMER_MAX_TOTAL_PIECES,
  customerDimensionMaxForUnit,
  expressPackMaxWeightForUnit,
  sumCustomerTotalPieces,
  toFiniteNumber,
  type PackagePieceRowGroup,
} from './forms/package-selection.form';
import type { PackageUnitSystem } from './package-types.config';

/** Shipment-level (not per-row) red-banner line. */
export interface CustomerBannerShipmentError {
  messageKey: string;
  params?: Record<string, string>;
}

/**
 * One per-package red-banner line.
 * Field-max lines use emphasisKey + detailKey (bold label + remainder); girth uses messageKey only.
 */
export interface CustomerBannerPackageMessage {
  /** Base key for params lookup; also the sole translate key when emphasis/detail are absent. */
  messageKey: string;
  emphasisKey?: string;
  detailKey?: string;
}

/** Per-package red-banner group (1-based package number). */
export interface CustomerBannerPackageGroup {
  packageNumber: number;
  messages: CustomerBannerPackageMessage[];
}

/** Structured Customer Packaging red-banner model for Add/Save. */
export interface CustomerPackagingBannerErrors {
  shipmentErrors: CustomerBannerShipmentError[];
  packageGroups: CustomerBannerPackageGroup[];
  showSurchargeFooter: boolean;
  hasBlockingErrors: boolean;
}

export interface CollectCustomerBannerErrorsOptions {
  unitSystem: PackageUnitSystem;
  /** Product Master `constraints.weight.max` in lb when available. */
  maxWeightLb?: number;
  /** Product Master `constraints.length.max` in inches when available. */
  maxLengthIn?: number;
  /** Product Master `constraints.girth.max` in inches when available. */
  maxGirthIn?: number;
  includeDefaultRowPieces?: boolean;
}

const DETAILS = 'createShipment.packageStep.details';

/**
 * Builds a field-max banner message with boldable emphasis + detail i18n keys.
 */
function fieldMaxMessage(baseKey: string): CustomerBannerPackageMessage {
  return {
    messageKey: `${DETAILS}.${baseKey}`,
    emphasisKey: `${DETAILS}.${baseKey}Emphasis`,
    detailKey: `${DETAILS}.${baseKey}Detail`,
  };
}

/**
 * True when Product Master supplied a usable numeric max for banner checks.
 */
function hasProductMasterMax(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Collects Add/Save red-banner errors (total pieces, per-row field max, girth).
 * Summed Total Weight is display-only and is not validated here.
 * Blank/required field errors are omitted (inline-only).
 * Girth blocking runs only when Product Master provides `girth.max`.
 */
export function collectCustomerPackagingBannerErrors(
  pieces: FormArray<PackagePieceRowGroup>,
  options: CollectCustomerBannerErrorsOptions,
): CustomerPackagingBannerErrors {
  const shipmentErrors: CustomerBannerShipmentError[] = [];
  const packageGroups: CustomerBannerPackageGroup[] = [];

  const totalPieces = sumCustomerTotalPieces(pieces) + (options.includeDefaultRowPieces ? 1 : 0);

  if (totalPieces > CUSTOMER_MAX_TOTAL_PIECES) {
    shipmentErrors.push({
      messageKey: 'createShipment.packageStep.details.totalPiecesExceedsLimit',
      params: { maxPieces: String(CUSTOMER_MAX_TOTAL_PIECES) },
    });
  }

  let showSurchargeFooter = false;

  pieces.controls.forEach((row, index) => {
    const messages: CustomerBannerPackageMessage[] = [];
    const weight = row.controls.perPieceWeight;
    const length = row.controls.length;
    const width = row.controls.width;
    const height = row.controls.height;

    if (weight.hasError('max')) {
      messages.push(fieldMaxMessage('perPieceWeightExceedsMax'));
      showSurchargeFooter = true;
    }

    if (length.hasError('max')) {
      messages.push(fieldMaxMessage('perPieceLengthExceedsMax'));
      showSurchargeFooter = true;
    }

    if (width.hasError('max')) {
      messages.push(fieldMaxMessage('perPieceWidthExceedsMax'));
      showSurchargeFooter = true;
    }

    if (height.hasError('max')) {
      messages.push(fieldMaxMessage('perPieceHeightExceedsMax'));
      showSurchargeFooter = true;
    }

    const lengthIn = lengthToInches(toFiniteNumber(length.value), options.unitSystem);
    const widthIn = lengthToInches(toFiniteNumber(width.value), options.unitSystem);
    const heightIn = lengthToInches(toFiniteNumber(height.value), options.unitSystem);

    if (hasProductMasterMax(options.maxGirthIn) && lengthIn > 0 && widthIn > 0 && heightIn > 0) {
      const girth = girthInches(lengthIn, widthIn, heightIn);
      if (girth > options.maxGirthIn) {
        messages.push({
          messageKey: 'createShipment.packageStep.details.overallDimensionExceedsMax',
        });
        showSurchargeFooter = true;
      }
    }

    if (messages.length > 0) {
      packageGroups.push({
        packageNumber: index + 1,
        messages,
      });
    }
  });

  return {
    shipmentErrors,
    packageGroups,
    showSurchargeFooter,
    hasBlockingErrors: shipmentErrors.length > 0 || packageGroups.length > 0,
  };
}

/**
 * Dual-unit params for per-piece weight exceed banner copy.
 */
export function customerWeightExceedParams(maxWeightLb: number): Record<string, string> {
  return {
    maxLb: String(expressPackMaxWeightForUnit('imperial', maxWeightLb)),
    maxKg: String(expressPackMaxWeightForUnit('metric', maxWeightLb)),
  };
}

/**
 * Dual-unit params for per-piece dimension exceed banner copy.
 */
export function customerDimensionExceedParams(maxLengthIn: number): Record<string, string> {
  return {
    maxIn: String(customerDimensionMaxForUnit('imperial', maxLengthIn)),
    maxCm: String(customerDimensionMaxForUnit('metric', maxLengthIn)),
  };
}
